import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Button,
  Label,
  SpinnerLoader,
  Toastify,
  SheetFooter,
  SingleSelect,
} from 'lift-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import {
  useAllowableValues,
  useBrandPlatforms,
  useCreateConfiguration,
  useReportConfigurations,
} from '../../hooks';
import type { ReportConfiguration, ConfigurationFormData } from '../../types';
import {
  CADENCES_WITH_FISCAL_CALENDAR,
  FISCAL_CALENDAR_WARNING,
  FISCAL_CALENDAR_DOCS_URL,
  DUPLICATE_ERROR_MESSAGE,
  DUPLICATE_DEACTIVATED_ERROR_MESSAGE,
} from '../../constants';
import { transformToAPI, transformToUI } from '../../utils/valueTransformations';
import { isBreakdownReport as isBreakdownReportType } from '../../utils/reportTypeUtils';
import { formatCreateConfigError } from '../../utils/errorMessage';
import {
  isAllowedCombination,
  findDuplicateConfiguration,
  shouldShowFiscalCalendar,
  validateFormData,
} from '../../utils/validations';

interface ConfigurationFormProps {
  brandId: number;
  brandName: string;
  hasFiscalCalendar?: boolean;
  existingConfigurations: ReportConfiguration[];
  onSuccess: () => void;
  onCancel: () => void;
}

const INITIAL_FORM_DATA: ConfigurationFormData = {
  brand_id: null,
  platform: null,
  report_type: null,
  granularity_level: '',
  metric_type: null,
  cadence: null,
  breakdown: null,
  use_fiscal_calendar: false,
};

interface LocalConfig extends ConfigurationFormData {
  localId: string;
  template_id?: number;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({
  brandId,
  brandName,
  hasFiscalCalendar = false,
  existingConfigurations,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ConfigurationFormData>({
    ...INITIAL_FORM_DATA,
    brand_id: brandId,
    use_fiscal_calendar: hasFiscalCalendar,
  });
  const [localConfigs, setLocalConfigs] = useState<LocalConfig[]>([]);
  const [showFiscalWarning, setShowFiscalWarning] = useState(false);

  const { data: allowableValues, isLoading: isLoadingAllowable } = useAllowableValues();
  const { data: brandPlatforms, isLoading: isLoadingPlatforms } = useBrandPlatforms(brandId);
  // Always use all configs (including deactivated) for duplicate checks, even if table filter is Active Only
  const { data: allBrandConfigurations } = useReportConfigurations(brandId, 'all');
  const { mutate: createConfig, isPending: isCreating } = useCreateConfiguration();

  const availablePlatforms = useMemo(() => {
    if (!brandPlatforms || !allowableValues) return [];
    
    // Get unique platforms from allowable values
    const allowablePlatformsSet = new Set<string>();
    allowableValues.forEach((av) => allowablePlatformsSet.add(av.platform));
    
    // Only show platforms that exist in both brand platforms AND allowable values
    return brandPlatforms.filter((platform) => allowablePlatformsSet.has(platform));
  }, [brandPlatforms, allowableValues]);

  const availableReportTypes = useMemo(() => {
    if (!allowableValues || !formData.platform) return [];
    const reportTypes = new Set<string>();
    const platformAPI = transformToAPI(formData.platform);
    
    allowableValues
      .filter((av) => {
        const avPlatformAPI = transformToAPI(av.platform);
        return avPlatformAPI === platformAPI;
      })
      .forEach((av) => {
        if (av.platform === 'Google Ads' && av.report_type === 'Age & Gender') {
          reportTypes.add('Age & Gender');
        } else {
          reportTypes.add(av.report_type);
        }
      });
    return Array.from(reportTypes);
  }, [allowableValues, formData.platform]);

  const availableGranularities = useMemo(() => {
    if (!allowableValues || !formData.platform || !formData.report_type) return [];
    const granularities = new Set<string>();
    const platformAPI = transformToAPI(formData.platform);
    const reportTypeAPI = transformToAPI(formData.report_type);

    allowableValues
      .filter((av) => {
        return transformToAPI(av.platform) === platformAPI &&
               transformToAPI(av.report_type) === reportTypeAPI;
      })
      .forEach((av) => granularities.add(av.granularity_level));
    return Array.from(granularities);
  }, [allowableValues, formData.platform, formData.report_type]);

  const isBreakdownReport = useMemo(() => {
    return isBreakdownReportType(formData.report_type);
  }, [formData.report_type]);

  const availableMetricTypes = useMemo(() => {
    if (!allowableValues || !formData.platform || !formData.report_type || !formData.granularity_level) return [];
    if (!isBreakdownReport) return [];

    const metricTypes = new Set<string>();
    const platformAPI = transformToAPI(formData.platform);
    const reportTypeAPI = transformToAPI(formData.report_type);
    const granularityAPI = transformToAPI(formData.granularity_level);

    allowableValues
      .filter((av) => {
        return transformToAPI(av.platform) === platformAPI &&
               transformToAPI(av.report_type) === reportTypeAPI &&
               transformToAPI(av.granularity_level) === granularityAPI &&
               av.metric_type && av.metric_type !== 'N/A';
      })
      .forEach((av) => {
        if (av.metric_type) metricTypes.add(av.metric_type);
      });
    return Array.from(metricTypes);
  }, [allowableValues, formData.platform, formData.report_type, formData.granularity_level, isBreakdownReport]);

  const availableCadences = useMemo(() => {
    if (!allowableValues || !formData.platform || !formData.granularity_level || isBreakdownReport) {
      return [];
    }
    const cadences = new Set<string>();
    const platformAPI = transformToAPI(formData.platform);
    const reportTypeAPI = transformToAPI(formData.report_type);
    const granularityAPI = transformToAPI(formData.granularity_level);
    
    allowableValues
      .filter((av) => {
        return transformToAPI(av.platform) === platformAPI && 
               transformToAPI(av.report_type) === reportTypeAPI &&
               transformToAPI(av.granularity_level) === granularityAPI &&
               av.cadence && av.cadence !== 'N/A';
      })
      .forEach((av) => {
        if (av.cadence) cadences.add(av.cadence);
      });
    return Array.from(cadences);
  }, [allowableValues, formData.platform, formData.report_type, formData.granularity_level, isBreakdownReport]);

  const showFiscalCalendarOption = useMemo(() => {
    return shouldShowFiscalCalendar(formData.report_type, formData.cadence);
  }, [formData.report_type, formData.cadence]);

  const handleFieldChange = useCallback((field: keyof ConfigurationFormData, value: unknown) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      if (field === 'platform') {
        newData.report_type = null;
        newData.granularity_level = '';
        newData.cadence = null;
        newData.metric_type = null;
        newData.use_fiscal_calendar = false;
      }

      if (field === 'report_type') {
        newData.granularity_level = '';
        newData.cadence = null;
        newData.metric_type = null;
        newData.use_fiscal_calendar = false;
      }

      if (field === 'granularity_level') {
        newData.cadence = null;
        newData.metric_type = null;
        newData.use_fiscal_calendar = false;
      }

      if (field === 'cadence') {
        const cadenceAPI = transformToAPI(value as string);
        if (!cadenceAPI || !['prior_month', 'month_to_date'].includes(cadenceAPI)) {
          newData.use_fiscal_calendar = false;
        } else if (hasFiscalCalendar) {
          newData.use_fiscal_calendar = true;
        }
      }

      if (field === 'use_fiscal_calendar' && value === true && !hasFiscalCalendar) {
        setShowFiscalWarning(true);
      } else {
        setShowFiscalWarning(false);
      }

      return newData;
    });
  }, [hasFiscalCalendar]);

  useEffect(() => {
    if (availableGranularities.length === 1 && formData.granularity_level !== availableGranularities[0]) {
      setFormData((prev) => ({ ...prev, granularity_level: availableGranularities[0] }));
    }
  }, [availableGranularities, formData.granularity_level]);

  useEffect(() => {
    if (availableMetricTypes.length === 1 && formData.metric_type !== availableMetricTypes[0]) {
      setFormData((prev) => ({ ...prev, metric_type: availableMetricTypes[0] }));
    }
  }, [availableMetricTypes, formData.metric_type]);

  const handleAddToList = useCallback(() => {
    if (!allowableValues) return;

    const { isValid, errors } = validateFormData(formData);
    if (!isValid) {
      errors.forEach((error) => Toastify('error', error));
      return;
    }

    const { isAllowed, templateId, message } = isAllowedCombination(allowableValues, formData);
    if (!isAllowed) {
      Toastify('error', message || 'This combination is not allowed');
      return;
    }

    const allConfigs = [
      ...(allBrandConfigurations || existingConfigurations),
      ...localConfigs.map((c) => ({
        ...c,
        brand_id: brandId,
        platform: transformToAPI(c.platform),
        report_type: transformToAPI(c.report_type),
        granularity_level: transformToAPI(c.granularity_level),
        metric_type: transformToAPI(c.metric_type),
        cadence: transformToAPI(c.cadence),
        status: 'active' as const,
        deactivation_at: null,
        reactivation_at: null,
      })),
    ];

    const duplicateConfig = findDuplicateConfiguration(
      allConfigs as ReportConfiguration[],
      formData
    );
    if (duplicateConfig) {
      const isDeactivatedDuplicate =
        duplicateConfig.status === 'deactivated' ||
        Boolean(
          duplicateConfig.deactivation_at &&
            new Date(duplicateConfig.deactivation_at) <= new Date() &&
            (!duplicateConfig.reactivation_at ||
              new Date(duplicateConfig.reactivation_at) > new Date())
        );
      Toastify(
        'error',
        isDeactivatedDuplicate
          ? DUPLICATE_DEACTIVATED_ERROR_MESSAGE
          : DUPLICATE_ERROR_MESSAGE
      );
      return;
    }

    const newConfig: LocalConfig = {
      ...formData,
      localId: `local-${Date.now()}`,
      template_id: templateId,
    };

    setLocalConfigs((prev) => [...prev, newConfig]);
    setFormData({
      ...INITIAL_FORM_DATA,
      brand_id: brandId,
      use_fiscal_calendar: hasFiscalCalendar,
    });
    setShowFiscalWarning(false);
  }, [
    allowableValues,
    formData,
    existingConfigurations,
    allBrandConfigurations,
    localConfigs,
    brandId,
    hasFiscalCalendar,
  ]);

  const handleRemoveFromList = useCallback((localId: string) => {
    setLocalConfigs((prev) => prev.filter((c) => c.localId !== localId));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (localConfigs.length === 0) {
      Toastify('error', 'Please add at least one configuration');
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const config of localConfigs) {
      try {
        await new Promise<void>((resolve, reject) => {
          createConfig(
            {
              brand_id: brandId,
              brand_name: brandName,
              platform: config.platform,
              report_type: config.report_type,
              granularity_level: config.granularity_level,
              metric_type: config.metric_type,
              cadence: config.cadence,
              breakdown: config.breakdown,
              use_fiscal_calendar: config.use_fiscal_calendar,
              template_id: config.template_id || 0,
            },
            {
              onSuccess: () => resolve(),
              onError: (error) => reject(error),
            }
          );
        });
        successCount++;
      } catch (error: unknown) {
        failCount++;
        Toastify(
          'error',
          formatCreateConfigError(error, {
            platform: config.platform,
            report_type: config.report_type,
          })
        );
      }
    }

    if (successCount > 0) {
      Toastify('success', successCount === 1 ? 'Configuration added successfully' : 'Configurations added successfully');
    }

    if (failCount === 0) {
      onSuccess();
    } else {
      setLocalConfigs((prev) => prev.slice(-failCount));
    }
  }, [localConfigs, brandId, createConfig, onSuccess]);

  const isFormComplete = useMemo(() => {
    if (!formData.platform || !formData.report_type || !formData.granularity_level) return false;
    
    if (isBreakdownReport) {
      return !!formData.metric_type;
    } else {
      return !!formData.cadence;
    }
  }, [formData, isBreakdownReport]);

  if (isLoadingAllowable || isLoadingPlatforms) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerLoader className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {/* Brand (read-only) */}
        <div className="space-y-1.5">
          <Label>Brand</Label>
          <div className="p-3 bg-gray-50 rounded-md text-sm font-medium">
            {brandName}
          </div>
        </div>

        {/* Platform */}
        <div className="space-y-1.5">
          <Label>Platform *</Label>
          <SingleSelect
            options={availablePlatforms.map((platform) => ({ label: platform, value: platform }))}
            defaultValue={formData.platform || undefined}
            onChange={(value) => handleFieldChange('platform', value)}
            placeholder="Select platform..."
            containerClassName="w-full"
          />
        </div>

        {/* Report Type */}
        {formData.platform && (
          <div className="space-y-1.5">
            <Label>Report Type *</Label>
            <SingleSelect
              options={availableReportTypes.map((reportType) => ({ label: reportType, value: reportType }))}
              defaultValue={formData.report_type || undefined}
              onChange={(value) => handleFieldChange('report_type', value)}
              placeholder="Select report type..."
              containerClassName="w-full"
            />
          </div>
        )}
        {!formData.platform && (
          <p className="text-xs text-gray-500 italic">Report type — select platform first</p>
        )}

        {/* Granularity */}
        {formData.report_type && availableGranularities.length > 1 && (
          <div className="space-y-1.5">
            <Label>Granularity *</Label>
            <SingleSelect
              options={availableGranularities.map((g) => ({ label: g, value: g }))}
              defaultValue={formData.granularity_level || undefined}
              onChange={(value) => handleFieldChange('granularity_level', value)}
              placeholder="Select granularity..."
              containerClassName="w-full"
            />
          </div>
        )}
        {formData.report_type && availableGranularities.length === 1 && (
          <div className="space-y-1.5">
            <Label>Granularity</Label>
            <div className="p-3 bg-gray-50 rounded-md text-sm">
              {formData.granularity_level}
            </div>
          </div>
        )}

        {/* Cadence (R&F only, after granularity selected) */}
        {formData.report_type && formData.granularity_level && !isBreakdownReport && (
          <div className="space-y-1.5">
            <Label>Cadence *</Label>
            <SingleSelect
              options={availableCadences.map((cadence) => ({ label: cadence, value: cadence }))}
              defaultValue={formData.cadence || undefined}
              onChange={(value) => handleFieldChange('cadence', value)}
              placeholder="Select cadence..."
              containerClassName="w-full"
            />
          </div>
        )}

        {/* Metric Type (Breakdowns only, after granularity selected) */}
        {formData.report_type && formData.granularity_level && isBreakdownReport && availableMetricTypes.length > 1 && (
          <div className="space-y-1.5">
            <Label>Metric Type *</Label>
            <SingleSelect
              options={availableMetricTypes.map((mt) => ({ label: mt, value: mt }))}
              defaultValue={formData.metric_type || undefined}
              onChange={(value) => handleFieldChange('metric_type', value)}
              placeholder="Select metric type..."
              containerClassName="w-full"
            />
          </div>
        )}
        {formData.report_type && formData.granularity_level && isBreakdownReport && availableMetricTypes.length === 1 && (
          <div className="space-y-1.5">
            <Label>Metric Type</Label>
            <div className="p-3 bg-gray-50 rounded-md text-sm">
              {formData.metric_type}
            </div>
          </div>
        )}

          {/* Fiscal Calendar Checkbox */}
          {showFiscalCalendarOption && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fiscal-calendar"
                  checked={formData.use_fiscal_calendar}
                  onCheckedChange={(checked) => handleFieldChange('use_fiscal_calendar', checked)}
                />
                <Label htmlFor="fiscal-calendar" className="cursor-pointer">
                  Include Fiscal Calendar Cadence
                </Label>
              </div>
              {showFiscalWarning && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800">{FISCAL_CALENDAR_WARNING}</p>
                    <a 
                      href={FISCAL_CALENDAR_DOCS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-yellow-900 underline hover:text-yellow-700 mt-1 inline-block"
                    >
                      View documentation
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
          
        {/* Add to List Button */}
        <Button
          type="button"
          variant="outline"
          onClick={handleAddToList}
          disabled={!isFormComplete}
          className="w-full flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add to List
        </Button>

        {/* Summary Table */}
        {localConfigs.length > 0 && (
          <div className="space-y-3">
            <Label>Configurations to Submit ({localConfigs.length})</Label>
            <div className="border rounded-md divide-y">
              {localConfigs.map((config) => (
                <div
                  key={config.localId}
                  className="flex items-center justify-between p-3 text-sm"
                >
                  <div className="space-y-1">
                    <div className="font-medium">{config.platform}</div>
                    <div className="text-gray-500">
                      {config.report_type} • {config.granularity_level}
                      {config.cadence && ` • ${config.cadence}`}
                      {config.metric_type && ` • ${config.metric_type}`}
                      {config.use_fiscal_calendar && ' • Fiscal Calendar'}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFromList(config.localId)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <SheetFooter className="border-t pt-4 px-6 pb-6">
        <div className="flex justify-end gap-2 w-full">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={localConfigs.length === 0 || isCreating}
          >
            {isCreating ? (
              <>
                <SpinnerLoader className="h-4 w-4 mr-2" />
                Submitting...
              </>
            ) : (
              `Submit ${localConfigs.length} Configuration${localConfigs.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </div>
      </SheetFooter>
    </div>
  );
};

export default ConfigurationForm;
